/*eslint-disable max-lines*/
import { ForensicsFilterValues } from '@hakimo-ui/hakimo/types';
import { ImageIcon } from '@hakimo-ui/hakimo/ui-elements';
import { Button } from '@hakimo-ui/shared/ui-base';
import { MagnifyingGlassIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import {
  defaultForensicsFilterValues,
  ForensicsFilter,
} from './ForensicFilter';

interface SearchQuery {
  inputValue: string;
  filters?: ForensicsFilterValues;
  selectedImage?: File;
}

interface Props {
  searchQuery: SearchQuery;
  updateSearchQuery: (searchQuery: SearchQuery) => void;
  isLoading: boolean;
  onSearch: () => void;
  setError: (msg: string | null) => void;
}

export const ForensicsFilterSection: FC<Props> = ({
  searchQuery,
  updateSearchQuery,
  isLoading,
  onSearch,
  setError,
}) => {
  const validateSearch = () => {
    if (searchQuery.inputValue.trim().length > 0 && !!selectedImage) {
      setError(
        'Search works with either text or an image, not both. Please remove one to continue.'
      );
      return;
    }
    setError(null);
    onSearch();
  };

  const [showImagePanel, setShowImagePanel] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(
    searchQuery.selectedImage ?? null
  );

  const [showImagePreview, setShowImagePreview] = useState(false);

  useEffect(() => {
    if (searchQuery.filters) {
      onSearch();
    }
  }, [searchQuery.filters]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSearchQuery({
      ...searchQuery,
      inputValue: e.target.value,
    });
  };

  const toggleImagePanel = () => setShowImagePanel(!showImagePanel);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);
      updateSearchQuery({ ...searchQuery, selectedImage: file });
      setShowImagePanel(false);
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    updateSearchQuery({ ...searchQuery, selectedImage: undefined });
  };

  return (
    <div className="px-[15%]">
      {/* Main Search Bar */}
      <div className="relative">
        <div className="dark:bg-ondark-bg-1 group flex items-center gap-2 rounded-2xl bg-white transition-all duration-200">
          {/*  Search Icon */}
          <div className="relative flex flex-1 items-center">
            <div className="relative w-full">
              {selectedImage && (
                <div className="absolute left-2 top-1/2 -translate-y-1/2">
                  <div className="relative">
                    <img
                      src={URL.createObjectURL(selectedImage)}
                      alt="uploaded"
                      className="h-6 w-6 cursor-pointer rounded-md border object-cover"
                      onClick={() => setShowImagePreview(true)}
                    />
                    <button
                      onClick={clearImage}
                      title="Remove image"
                      className="text-onlight-text-2 absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-white text-[10px] font-bold shadow-md hover:bg-gray-100"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}

              <input
                type="search"
                placeholder="Search alarms using AI — e.g., 'person in red shirt' or 'yellow car'"
                value={searchQuery.inputValue}
                onChange={onInputChange}
                onKeyDown={(e) => e.key === 'Enter' && validateSearch()}
                autoFocus
                className={clsx(
                  'text-onlight-text-1 placeholder-onlight-text-3 dark:text-ondark-text-1 dark:placeholder-ondark-text-3 w-full rounded-md bg-transparent py-2 pr-20 text-sm font-medium outline-none',
                  selectedImage ? 'pl-10' : 'pl-2'
                )}
              />

              <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5 pr-0.5">
                <button
                  onClick={toggleImagePanel}
                  type="button"
                  title="Search by Image"
                  className={clsx(
                    'rounded-md p-1.5 transition-all duration-200 hover:scale-105',
                    showImagePanel
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-onlight-text-3 dark:text-ondark-text-2 hover:text-onlight-text-1 dark:hover:text-ondark-text-1'
                  )}
                >
                  <PhotoIcon className="h-7 w-7 transition-colors" />
                </button>

                <button
                  onClick={validateSearch}
                  disabled={
                    (!searchQuery.inputValue.trim() && !selectedImage) ||
                    isLoading
                  }
                  title="Search"
                  className={clsx(
                    'bg-primary-600 hover:bg-primary-700 rounded-md p-0.5 px-1.5 text-white transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50',
                    'dark:bg-primary-500 dark:hover:bg-primary-400'
                  )}
                >
                  {isLoading ? (
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <MagnifyingGlassIcon className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <ForensicsFilter
              filterValues={searchQuery.filters ?? defaultForensicsFilterValues}
              setFilterValues={(filters) => {
                updateSearchQuery({
                  ...searchQuery,
                  filters,
                });
              }}
            />
          </div>
        </div>

        {showImagePanel && (
          <div className="relative w-full">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('ring-2', 'ring-primary-600');
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('ring-2', 'ring-primary-600');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('ring-2', 'ring-primary-600');
                const file = e.dataTransfer.files?.[0];
                if (file && file.type.startsWith('image/')) {
                  setSelectedImage(file);
                  updateSearchQuery({ ...searchQuery, selectedImage: file });
                  setShowImagePanel(false);
                }
              }}
              className="bg-onlight-bg-3 dark:bg-ondark-bg-2 absolute left-0 right-0 top-full z-30 rounded-2xl p-20 text-center shadow-lg transition-all duration-200"
            >
              <div className="flex flex-row items-center justify-center gap-3">
                <ImageIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-10 w-10 flex-shrink-0" />
                <p className="text-onlight-text-3 dark:text-ondark-text-3 text-sm">
                  Drag an image here or{' '}
                  <label className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 cursor-pointer underline">
                    upload a file
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageSelect}
                    />
                  </label>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {showImagePreview && selectedImage && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowImagePreview(false)}
        >
          <div
            className="bg-onlight-bg-2 dark:bg-ondark-bg-2 absolute left-96 top-20 max-h-svh max-w-2xl rounded-lg p-1"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="icon"
              onClick={() => setShowImagePreview(false)}
              className="absolute right-0.5 top-0.5 !p-1"
              title="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </Button>
            <img
              src={URL.createObjectURL(selectedImage)}
              alt="preview"
              className="rounded-md object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};
